import { ImportPipelineConnectorSettingsFragment } from '@warebee/frontend/data-access-api-graphql';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { cloneWithoutTypename } from '../common/utils';
import { Button } from '../components/actions/Button';
import * as Icon from '../components/icons';
import KeyValueItem from '../components/KeyValueItem';
import { ActionBar } from '../components/nav/ActionBar';
import Callout from '../helpContext/Callout';
import useUpdateImportPipeline from './hooks/useUpdateImportPipeline';
import {
  importPipelineIsActiveEditor,
  importPipelineSelected,
  importPipelineSelectedConnectorId,
} from './store/importPipelines.state';

export type ImportPipelineSFTPConnectorProps = {
  pipelineId: string;
  connector: ImportPipelineConnectorSettingsFragment;
};

const ImportPipelineSFTPConnector: React.FC<
  ImportPipelineSFTPConnectorProps
> = props => {
  const { t } = useTranslation('dataset');
  const pipeline = useRecoilValue(importPipelineSelected);
  const [selectedId, setSelectedId] = useRecoilState(
    importPipelineSelectedConnectorId,
  );

  const setShowEditor = useSetRecoilState(importPipelineIsActiveEditor);
  const updatePipeline = useUpdateImportPipeline();

  const [showPassword, setShowPassword] = React.useState(false);
  const [showEditor] = React.useState(false);

  function removeConnectorById(id: string) {
    const connectors = _(pipeline.settings?.integrationSettings?.connectors)
      .filter(c => c.id !== id)
      .map(cloneWithoutTypename)
      .value();

    updatePipeline({
      importPipelineId: pipeline.id,
      settings: {
        ...pipeline.settings,
        integrationSettings: {
          connectors,
        },
      },
    });
  }

  const { id, sftpPull: sftp } = props.connector;
  const isActive = id === selectedId;

  return (
    <div>
      <ActionBar className="flex gap-2 p-2">
        <Button
          className="rounded text-xs"
          buttonType="secondary"
          buttonSize="xs"
          hasIconAfter={<Icon.Show className="mx-2 h-4 w-4 fill-current" />}
          onPress={e => {
            setShowPassword(v => !v);
          }}
        >
          {showPassword ? t`Hide Password` : t`Show Password`}
        </Button>

        <Button
          className="rounded text-xs"
          buttonType="secondary"
          buttonSize="xs"
          // hasIconAfter={<Icon. className="mx-2 h-4 w-4 fill-current" />}
          onPress={e => {
            setShowEditor(true);
            setSelectedId(isActive ? null : id);
          }}
        >
          {showEditor ? t`Cancel` : t`Edit`}
        </Button>
      </ActionBar>
      {/* <RuleContainer
        dataComponent="ImportPipelineSFTPConnector"
        key={`'pipeline-connector-'-${id}`}
        id={`'pipeline-connector-'-${id}`}
        title={t`SFTP Connector`}
        hasIcon={true}
        hasColorMode={false}
        isCollapsible={true}
        isActive={isActive}
        // onClick={() => {
        //   setShowEditor(true);
        //   setSelectedId(isActive ? null : id);
        // }}
        isRemovable={true}
        onDeleteClick={() => removeConnectorById(id)}
        className={classNames('p-1 xl:p-2')}
        transparent
        classNameInner={classNames('space-y-2 xl:space-y-4')}
      > */}

      <div className="p-3">
        <Callout type="suggestion" className="!bg-app-panel/20 p-3">
          <div>{t`SFTP command to upload a file to the server:`}</div>
          <div className="flex flex-col gap-4">
            {sftp.password && (
              <KeyValueItem
                keyName={t`Password`}
                keyValue={showPassword ? sftp.password : '••••••••'}
                isCopyable
                isDisabled={true}
                className="rounded"
              />
            )}

            <KeyValueItem
              keyName={t`SFTP Command Line test`}
              keyValue={`sftp -P ${sftp.port} ${sftp.username}@${sftp.host}\nput file.gz ${sftp.path}`}
              isCopyable
              isDisabled={true}
              className="rounded"
            />
            <KeyValueItem
              keyName={t`SFTP Client Setup Example (OpenSSH)`}
              keyValue={`Host warebee-sftp-pipeline\n
            HostName ${sftp.host}\n
            Port ${sftp.port}\n
            User ${sftp.username}\n
            Password ${showPassword ? sftp.password : '••••••••'}\n
            // IdentityFile ~/.ssh/id_rsa\n
            \n # Example:
            sshpass -p '${showPassword ? sftp.password : '••••••••'}' sftp -P ${sftp.port} ${sftp.username}@${sftp.host}`}
              isCopyable
              isDisabled={true}
              className="rounded"
            >
              <span className="text-xxs mt-1 p-2 opacity-50">{t`Add this to your ~/.ssh/config for easier SFTP access.`}</span>
              <span className="text-xxs mt-1 block p-2 opacity-50">
                {t`Note: OpenSSH config does not support a Password field. Use this for reference only. For scripting, you can use sshpass:`}
                <br />
              </span>
            </KeyValueItem>
            <KeyValueItem
              keyName={t`SFTP Client Setup Example (FileZilla)`}
              keyValue={`
              <Server>
              <Host>${sftp.host}</Host>
              <Port>${sftp.port}</Port>
              <User>${sftp.username}</User>
              <Password>${showPassword ? sftp.password : '••••••••'}</Password>
              <Protocol>SFTP</Protocol>
              <Logontype>Key file</Logontype>
              <Keyfile>/path/to/id_rsa</Keyfile>
              <RemoteDir>${sftp.path}</RemoteDir>
              </Server>`}
              isCopyable
              isDisabled={true}
              className="rounded"
            >
              <span className="text-xxs mt-1 p-2 opacity-50">{t`Import this XML in FileZilla or create a new SFTP site with these values.`}</span>
            </KeyValueItem>
            <KeyValueItem
              keyName={t`SFTP Client Setup Example (WinSCP)`}
              keyValue={`[Sessions/my-warebee-sftp]\nHostName=${sftp.host}\nPortNumber=${sftp.port}\nUserName=${sftp.username}\nPassword=${showPassword ? sftp.password : '••••••••'}\nProtocol=3\nPrivateKeyFile=/path/to/id_rsa\nRemoteDirectory=${sftp.path}`}
              isCopyable
              isDisabled={true}
              className="rounded"
            >
              <span className="text-xxs mt-1 p-2 opacity-50">
                {t`Import this INI in WinSCP or create a new session with these values.`}
              </span>
            </KeyValueItem>
            <KeyValueItem
              keyName={t`SFTP Client Setup Example (Cyberduck)`}
              keyValue={`sftp://${sftp.username}:${showPassword ? sftp.password : '••••••••'}@${sftp.host}:${sftp.port}${sftp.path}`}
              isCopyable
              isDisabled={true}
              className="rounded"
            >
              <span className="text-xxs mt-1 p-2 opacity-50">
                {t`Open Cyberduck, click "Open Connection", choose SFTP, and fill in the details.`}
              </span>
            </KeyValueItem>
          </div>
        </Callout>
      </div>
      {/* <PolicyFilterItem
        name={t`Server`}
        value={`${sftp.host}:${sftp.port}${sftp.path}`}
        isActive={isActive}
        isDisabled={true}
      />
      <PolicyFilterItem
        name={t`File mask`}
        value={`${sftp.pattern}`}
        isActive={isActive}
        isDisabled={true}
      /> */}
      {/* </RuleContainer> */}
    </div>
  );
};

export default ImportPipelineSFTPConnector;
